// Resource Gantt — 포커스된 리소스의 가동률 그리드 (AG Grid, MP Review 스타일).
// 가로축(열)=날짜(버킷), 세로축(행)=가동률 지표(총/사용/여유/초과/부하율).
// 값 단위는 S/M/H(ms→변환), 부하율은 비율(%표시).

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import { AgGridReact } from "ag-grid-react";

import NumberUtil from "@plannel/utils/number-util";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import { OVERLOAD_COLOR, isOverloaded } from "@plannel/pages/master-plan/resource-gantt/ganttConstants";
import { bucketCap, bucketUsed } from "@plannel/pages/master-plan/resource-gantt/ganttScales";

const UOM_DIV = { seconds: 1, minutes: 60, hour: 3600 }; // capacity는 초(seconds) 단위로 저장

// 셀 값 포맷 — util 행은 %(비율), 그 외는 소수. (행의 _type 기준)
const cellFormatter = (p) => {
  const v = p.value;
  if (v == null || !isFinite(v)) return "";
  if (p.data?._type === "util") {
    // 소수 2자리 — 주문 사양 패널(OrderSpecPanel)의 부하율 표기와 자리수를 맞춘다.
    return NumberUtil.formatNumberData(p, { style: "percent", maximumFractionDigits: 2, minimumFractionDigits: 2 });
  }
  return NumberUtil.formatNumberData(p, { maximumFractionDigits: 2, minimumFractionDigits: 1 });
};

const ResourceUtilTable = ({ buckets = [], bucketLabels = {}, timeUom = "seconds", title = "" }) => {
  const { t } = useTranslation();
  const div = UOM_DIV[timeUom] || UOM_DIV.minutes;

  const gridProps = useMemo(() => DefaultGridSetting({ title: title || t("utilization") }), [t, title]);

  // 버킷별 지표값
  const perBucket = useMemo(
    () =>
      buckets.map((b) => {
        const total = bucketCap(b);
        const used = bucketUsed(b);
        return {
          key: b.key,
          total: total / div,
          used: used / div,
          avail: Math.max(total - used, 0) / div,
          // 초과 판정은 캔버스·패널과 동일하게 허용오차(isOverloaded)를 적용한다 —
          // 부동소수 잔차 수준의 미세 초과를 위/아래에서 다르게 표시하지 않도록.
          over: isOverloaded(used, total) ? Math.max(used - total, 0) / div : 0,
          util: total > 0 ? used / total : used > 0 ? 1 : 0, // 비율
        };
      }),
    [buckets, div]
  );

  // 행 = 지표(총/사용/여유/초과/부하율), 열 = 버킷
  const measures = useMemo(
    () => [
      { key: "total", name: t("totalCapacity") },
      { key: "used", name: t("usedCapacity") },
      { key: "avail", name: t("remainCapacity") },
      { key: "over", name: t("overload") },
      { key: "util", name: t("utilization") },
    ],
    [t]
  );

  const rowData = useMemo(
    () =>
      measures.map((m) => {
        const row = { measure: m.name, _type: m.key };
        perBucket.forEach((pb) => {
          row[pb.key] = pb[m.key];
        });
        return row;
      }),
    [measures, perBucket]
  );

  const columnDefs = useMemo(
    () => [
      { headerName: t("utilization"), field: "measure", pinned: "left", width: 130, type: ["nonEditableColumn"], cellClass: "stringType" },
      ...buckets.map((b) => ({
        headerName: bucketLabels[b.key] ?? b.key,
        field: b.key,
        width: 116,
        wrapHeaderText: false,
        sortable: false,
        type: ["nonEditableColumn", "rightAligned"],
        valueFormatter: cellFormatter,
        cellStyle: (p) => (p.data?._type === "over" && p.value > 0 ? { color: OVERLOAD_COLOR } : null),
      })),
    ],
    [buckets, bucketLabels, t]
  );

  return (
    <Box className="ag-theme-balham grid-area non-background-selected-row" sx={{ height: "100%", width: "100%" }}>
      <AgGridReact {...gridProps} rowData={rowData} columnDefs={columnDefs} />
    </Box>
  );
};

export default ResourceUtilTable;
